import React, { useEffect, useRef, useState } from "react";
import "./CertifiedDesign2.css";

const MA_APPLY_URL = "https://admissions.eusbd.eu/esbd-apply/MA-GBSL/";

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path
      d="M4 6l4 4 4-4"
      stroke="#0C1F3F"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M4 12L12 4M12 4H6M12 4v6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function CertifiedDesign2CoursePage({ pageData, overviewImageSrc }) {
  const [openModules, setOpenModules] = useState({});
  const [activeAnchor, setActiveAnchor] = useState("overview");
  const particlesRef = useRef(null);

  const toggleModule = (idx) => setOpenModules((p) => ({ ...p, [idx]: !p[idx] }));

  useEffect(() => {
    const container = particlesRef.current;
    if (!container) return;

    // Prevent duplicate particles if React re-renders/mounts.
    container.innerHTML = "";

    const types = ["", "--line"];
    for (let i = 0; i < 30; i++) {
      const p = document.createElement("div");
      const t = types[Math.floor(Math.random() * 2)];
      p.className = "particle particle" + t;
      const s = Math.random() * 5 + 3;
      p.style.cssText =
        "left:" +
        Math.random() * 100 +
        "%;width:" +
        s +
        "px;height:" +
        s +
        "px;background:rgba(197,164,78," +
        (Math.random() * 0.2 + 0.08) +
        ");animation-duration:" +
        (Math.random() * 10 + 6) +
        "s;animation-delay:" +
        Math.random() * 8 +
        "s";
      container.appendChild(p);
    }
  }, []);

  useEffect(() => {
    const ids = [
      "overview",
      "highlights",
      "modules",
      "admissions",
      "fees",
      "careers",
      "attractions",
    ];
    const onScroll = () => {
      const y = window.scrollY + 160;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= y && el.offsetTop + el.offsetHeight > y) {
          setActiveAnchor(id);
        }
      }
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!pageData) return null;

  const {
    heroTitleEm,
    programmeDetails,
    overviewParagraphs,
    overviewImageAlt,
    highlights,
    modules,
    careers,
    careersIntro,
    keyAttractions,
    ctaHeading,
    ctaParagraph,
    iconMap,
    careersIconKey,
  } = pageData;

  return (
    <div className="cert2c-page">
      {/* HERO */}
      <section className="hero" aria-labelledby="certified-design2-hero-title">
        <div className="hero__particles" ref={particlesRef} />
        <div className="hero__orbs">
          <div className="hero__orb hero__orb--1" />
          <div className="hero__orb hero__orb--2" />
        </div>
        <div className="hero__beam" />

        <div className="hero__inner">
          <div className="hero__left">
            <h1 className="hero__title" id="certified-design2-hero-title">
              Welcome To The <em>{heroTitleEm}</em> Programme
            </h1>

            <div className="hero__btns">
              <a href={MA_APPLY_URL} target="_blank" rel="noreferrer" className="hero__btn hero__btn--gold">
                Apply Now
              </a>
              <a href="#" className="hero__btn hero__btn--ghost">
                Download Programme Brochure
              </a>
            </div>

            <div className="hero__details">
              {programmeDetails.map((d, i) => (
                <div key={i} className="hero__det">
                  <div>
                    <div className="hero__det-label">{d.label}</div>
                    <div className="hero__det-value">{d.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hero__right">
            <div className="hero__video">
              <iframe
                src="https://www.youtube.com/embed/BgdE65SZ1SU?rel=0"
                title="ESBD"
                allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ANCHOR NAV */}
      <div className="anchor">
        <div className="anchor__in">
          {[
            ["overview", "Overview"],
            ["highlights", "Highlights"],
            ["modules", "Modules"],
            ["admissions", "Admissions"],
            ["fees", "Fees"],
            ["careers", "Career Opportunities"],
            ["attractions", "Key Attractions"],
          ].map(([id, label]) => (
            <a
              key={id}
              href={`#${id}`}
              className={activeAnchor === id ? "active" : ""}
              onClick={(e) => scrollToSection(e, id)}
            >
              {label}
            </a>
          ))}
        </div>
      </div>

      {/* OVERVIEW */}
      <div className="s" id="overview">
        <div className="ov-grid">
          <div className="ov-text">
            <div className="sh">
              <h2>Programme Overview</h2>
            </div>
            {overviewParagraphs.map((p, idx) => (
              <p key={idx}>{p}</p>
            ))}
          </div>
          <div className="ov-img">
            <img
              src={overviewImageSrc}
              alt={overviewImageAlt}
              onError={(e) => {
                // Keep the section background visible if the image path is missing.
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        </div>
      </div>

      {/* HIGHLIGHTS */}
      <div className="s s--f s--a" style={{ padding: "64px 0" }} id="highlights">
        <div className="si">
          <div className="sh sh--c">
            <h2>Programme Highlights</h2>
          </div>
          <div className="hl-grid">
            {highlights.map((h, i) => (
              <div key={i} className="hl">
                <div className="hl__icon">{iconMap[h.icon]}</div>
                <div className="hl__title">{h.title}</div>
                <div className="hl__text">{h.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* MODULES */}
      <div className="s" id="modules">
        <div className="sh sh--c">
          <h2>Programme Modules</h2>
        </div>
        {modules.map((m, i) => (
          <div
            key={i}
            className={`mod-acc ${openModules[i] ? "open" : ""}`}
            onClick={() => toggleModule(i)}
          >
            <div className="mod-acc__head">
              <div className="mod-acc__title">
                <span>{i + 1}</span> {m.title}
              </div>
              <div className="mod-acc__icon">
                <ChevronDown />
              </div>
            </div>
            <div className="mod-acc__body">
              <div className="mod-acc__content">{m.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ADMISSIONS */}
      <div className="s s--f s--a" style={{ padding: "64px 0" }} id="admissions">
        <div className="si">
          <div className="sh sh--c">
            <h2>Admissions Requirements</h2>
          </div>
          <div className="adm-grid">
            <div className="adm-card">
              <h3>
                <span>Eligibility</span> Eligibility Criteria
              </h3>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--navy)",
                  marginBottom: 8,
                  paddingBottom: 8,
                  borderBottom: "1px solid #F0F0EC",
                }}
              >
                Route 1: Standard Entry
              </p>
              <div className="adm-item">
                <div className="adm-dot" />
                <p>
                  <strong>Undergraduate Degree:</strong> Holders of an
                  undergraduate degree, diploma, or equivalent qualification from
                  a recognised institution.
                </p>
              </div>
              <div className="adm-item">
                <div className="adm-dot" />
                <p>
                  <strong>Proficiency in English:</strong> Demonstrated
                  proficiency in English communication.
                </p>
              </div>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--navy)",
                  marginBottom: 8,
                  marginTop: 16,
                  paddingBottom: 8,
                  borderBottom: "1px solid #F0F0EC",
                }}
              >
                Route 2: Experience-Based Entry (Non-Degree Holders)
              </p>
              <div className="adm-item">
                <div className="adm-dot" />
                <p>
                  <strong>Educational Requirement:</strong> Completed Senior
                  Secondary or equivalent qualification. Malta: SEC/Matriculation
                  Certificate (MQF Level 4); UK: A-Levels or BTEC National Diploma;
                  other countries: High School Diploma, Baccalaureate, or
                  equivalent national secondary leaving certificate.
                </p>
              </div>
              <div className="adm-item">
                <div className="adm-dot" />
                <p>
                  <strong>Professional Experience:</strong> A minimum of 10 years
                  of relevant professional work experience in a related field.
                </p>
              </div>
              <div className="adm-item">
                <div className="adm-dot" />
                <p>
                  <strong>Proficiency in English:</strong> Demonstrated
                  proficiency in English communication.
                </p>
              </div>
            </div>

            <div className="adm-card">
              <h3>
                <span>English</span> English Language Requirements
              </h3>
              <p
                style={{
                  fontSize: 13.5,
                  color: "var(--text-body)",
                  lineHeight: 1.6,
                  marginBottom: 12,
                }}
              >
                Applicants must meet one of the following requirements:
              </p>
              <div className="adm-item">
                <div className="adm-dot" />
                <p>Native English speakers</p>
              </div>
              <div className="adm-item">
                <div className="adm-dot" />
                <p>TOEFL (250 computer-based, 79/80 internet-based, or 550 paper-based)</p>
              </div>
              <div className="adm-item">
                <div className="adm-dot" />
                <p>IELTS (minimum score 6.0)</p>
              </div>
              <div className="adm-item">
                <div className="adm-dot" />
                <p>Cambridge CAE (C1, minimum score C)</p>
              </div>
              <div className="adm-item">
                <div className="adm-dot" />
                <p>Proof of English as a language of instruction</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FEES */}
      <div className="s" id="fees">
        <div className="sh sh--c">
          <h2>Programme Fees</h2>
        </div>
        <div className="fee-cards">
          <div
            style={{
              position: "relative",
              borderRadius: 18,
              overflow: "hidden",
              border: "2px solid var(--gold)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 16,
                left: "50%",
                transform: "translateX(-50%)",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                padding: "5px 14px",
                borderRadius: 6,
                background: "var(--gold)",
                color: "var(--navy)",
                zIndex: 1,
              }}
            >
              Best Choice
            </div>
            <div
              style={{
                background: "var(--gold)",
                padding: "52px 28px 32px",
                textAlign: "center",
              }}
            >
              <h3
                style={{
                  fontFamily: "var(--serif)",
                  fontSize: 20,
                  color: "var(--navy)",
                  marginBottom: 20,
                }}
              >
                One-Time Payment
              </h3>
              <div style={{ borderTop: "2px solid rgba(12,31,63,0.15)", paddingTop: 20 }}>
                <div
                  style={{
                    fontFamily: "var(--serif)",
                    fontSize: 44,
                    fontWeight: 700,
                    color: "var(--navy)",
                    marginBottom: 4,
                  }}
                >
                  &euro;650
                </div>
                <div style={{ fontSize: 13, color: "rgba(12,31,63,0.5)", marginBottom: 16, textDecoration: "line-through" }}>
                  &euro;750
                </div>
                <p style={{ fontSize: 13, color: "rgba(12,31,63,0.65)", lineHeight: 1.6, marginBottom: 12 }}>
                  Pay the full programme fee upfront and receive exclusive access
                  to a leadership webinar with an international expert.
                </p>
                <div
                  style={{
                    display: "inline-block",
                    background: "var(--navy)",
                    color: "var(--gold-light)",
                    fontSize: 12,
                    fontWeight: 700,
                    padding: "6px 16px",
                    borderRadius: 6,
                    letterSpacing: 0.5,
                    marginBottom: 8,
                  }}
                >
                  SAVE &euro;100
                </div>
                <p style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", marginTop: 8 }}>
                  Pay once &amp; save!
                </p>
              </div>
            </div>
          </div>

          <div style={{ position: "relative", borderRadius: 18, overflow: "hidden", border: "1px solid #ECECEA" }}>
            <div
              style={{
                position: "absolute",
                top: 16,
                left: "50%",
                transform: "translateX(-50%)",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                padding: "5px 14px",
                borderRadius: 6,
                background: "var(--off-white)",
                border: "1px solid #ECECEA",
                color: "var(--navy)",
                zIndex: 1,
              }}
            >
              Flexible
            </div>
            <div
              style={{
                background: "var(--white)",
                padding: "52px 28px 32px",
                textAlign: "center",
              }}
            >
              <h3
                style={{
                  fontFamily: "var(--serif)",
                  fontSize: 20,
                  color: "var(--navy)",
                  marginBottom: 20,
                }}
              >
                Instalment Plan
              </h3>
              <div style={{ borderTop: "1px solid #F0F0EC", paddingTop: 20 }}>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontFamily: "var(--serif)", fontSize: 44, fontWeight: 700, color: "var(--gold)" }}>
                    &euro;250
                  </span>
                  <span style={{ fontSize: 14, color: "var(--text-body)", marginLeft: 6 }}>
                    per instalment
                  </span>
                </div>
                <p style={{ fontSize: 14, color: "var(--text-body)", marginBottom: 4 }}>(3 Instalments)</p>
                <p style={{ fontSize: 12, color: "#999", marginBottom: 16 }}>1st: &euro;250 &bull; 2nd: &euro;250 &bull; 3rd: &euro;250</p>
                <p style={{ fontSize: 13, color: "var(--navy)", fontWeight: 600 }}>Total: &euro;750</p>
                <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 12 }}>
                  {["1st", "2nd", "3rd"].map((n) => (
                    <div
                      key={n}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: "var(--gold-15)",
                        border: "1px solid rgba(197,164,78,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 700,
                        color: "var(--gold)",
                      }}
                    >
                      {n}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CAREERS */}
      <div className="s s--f s--n" style={{ padding: "64px 0" }} id="careers">
        <div className="si">
          <div className="sh sh--c">
            <h2>Career Opportunities</h2>
            <p>{careersIntro}</p>
          </div>

          <div className="car-grid">
            {careers.map((c, i) => (
              <div key={i} className="car">
                <div className="car__icon">{iconMap[careersIconKey]}</div>
                <div className="car__title">{c.title}</div>
                <div className="car__text">{c.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* KEY ATTRACTIONS */}
      <div className="s" id="attractions">
        <div className="sh sh--c">
          <h2>Key Attractions</h2>
        </div>
        <div className="ka-grid">
          {keyAttractions.map((k, i) => (
            <div key={i} className="ka">
              <div className="ka__dot" />
              <div className="ka__title">{k.title}</div>
              <div className="ka__text">{k.text}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="cta">
        <h2>{ctaHeading}</h2>
        <p>{ctaParagraph}</p>
        <a href={MA_APPLY_URL} target="_blank" rel="noreferrer">
          Apply Now <ArrowIcon />
        </a>
      </div>
    </div>
  );
}

